import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';
import ReactDOMServer from 'react-dom/server';

import {colors} from '../../../../colors/colors';
import {FileIcon, ClockIcon} from '../../../../icon';
import {GlobeCursorRegularIcon} from '../../../../icon/svg/globe-cursor-regular-icon';
import {StackLegacy} from '../../../../layout/stack/stack-legacy';
import {DurationTimestamp} from '../../../../timestamp/duration-timestamp';

import {truncateSessionPages} from './truncate-session-pages';
import type {TruncatablePage} from './truncate-session-pages';

import styles from './truncated-session-content.module.css';

const MAX_PAGES_DISPLAYED = 3;

type Page = TruncatablePage;

type Props = {
    durationSeconds: number;
    pages: Page[];
    maUrlCount?: number;
};

export function TruncatedSessionContent(props: Props) {
    const {durationSeconds, pages, maUrlCount = 0} = props;

    React.useEffect(() => {
        ReactTooltip.rebuild();

        return () => ReactTooltip.hide();
    }, []);

    const {visiblePages, hiddenPages} = truncateSessionPages(pages, MAX_PAGES_DISPLAYED);
    const lastVisiblePage = visiblePages[visiblePages.length - 1];

    const renderPageItem = (page: Page) => (
        <div
            key={page.id}
            className={
                page.maUrl
                    ? classnames(styles['page-item'], styles['page-item--ma-url'])
                    : styles['page-item']
            }
        >
            {page.maUrl ? (
                <GlobeCursorRegularIcon
                    size={14}
                    fill={colors.secondarySidebarHoverBlue}
                    wrapWithDiv={false}
                />
            ) : null}
            <span>{page.path ? page.path : <i>Unknown page</i>}</span>
        </div>
    );

    return (
        <>
            <div className={styles['truncated-session-content']}>
                {pages.length > MAX_PAGES_DISPLAYED ? (
                    <>
                        {visiblePages.slice(0, -1).map(renderPageItem)}
                        {hiddenPages.length > 0 ? (
                            <div
                                data-html={true}
                                data-tip={ReactDOMServer.renderToString(
                                    <div>
                                        {hiddenPages.map((page, index) => (
                                            <div
                                                key={
                                                    page.path ? page.path : `unknown-page-${index}`
                                                }
                                            >
                                                {page.path ? page.path : <i>Unknown page</i>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                data-place='top'
                                className={styles['hidden-pages']}
                            >
                                {hiddenPages.length} more page{hiddenPages.length !== 1 ? 's' : ''}
                            </div>
                        ) : null}
                        {lastVisiblePage ? renderPageItem(lastVisiblePage) : null}
                    </>
                ) : (
                    visiblePages.map(renderPageItem)
                )}
            </div>
            <div className={styles['info-bar']}>
                <StackLegacy spacing={16} isHorizontal={true}>
                    <div
                        data-tip={`${pages.length} page${pages.length !== 1 ? 's' : ''}`}
                        data-place='top'
                    >
                        <StackLegacy spacing={8} isHorizontal={true}>
                            <FileIcon size={15} wrapWithDiv={false} fill={colors.greyLt} />
                            {pages.length}
                        </StackLegacy>
                    </div>
                    <StackLegacy spacing={8} isHorizontal={true}>
                        <ClockIcon size={17} wrapWithDiv={false} />
                        <DurationTimestamp durationSeconds={durationSeconds} />
                    </StackLegacy>
                    {maUrlCount > 0 ? (
                        <div
                            data-tip={`${maUrlCount} marketing automation URL${
                                maUrlCount !== 1 ? 's' : ''
                            } visited`}
                            data-place='top'
                        >
                            <StackLegacy spacing={8} isHorizontal={true}>
                                <GlobeCursorRegularIcon
                                    size={15}
                                    fill={colors.secondarySidebarHoverBlue}
                                    wrapWithDiv={false}
                                />
                                {maUrlCount}
                            </StackLegacy>
                        </div>
                    ) : null}
                </StackLegacy>
            </div>
        </>
    );
}
